import { Chain, getResultFromResponse, retryWithFibonacci, runAsyncSafe } from '@web3/utils';
import { useMemo } from 'react';
import { EventBasedStore, pickStoreActions } from '../utils/EventBasedStore';
import { queryChainList } from '../api';
import { getEvmRpcProvider } from '../../entry_inner';
import { onlyRunOnce } from '../wallet-hub/misc';
import { ChainTypes } from '../wallet-hub/core/types';

type ChainListState = {
  loading: boolean;
  // 云钱包链列表
  chainList: Chain[];
  // mpc 钱包链列表
  mpcChainList: Chain[];
  // 助记词钱包链列表
  mnemonicChainList: Chain[];
  showUpdateDialog: boolean;
};

const compute = (state: ChainListState) => {
  const makeChainMap = (chainList: Chain[]) =>
    chainList.reduce(
      (acc, chain) => {
        acc[chain.chainCode.toUpperCase()] = chain;
        acc[chain.chainCode.toLowerCase()] = chain;
        acc[chain.chainId] = chain;
        return acc;
      },
      {} as Record<string, Chain>,
    );
  const mixedChainId: Record<string, Chain> = {};
  const mixedChainMap: Record<string, Chain> = {};

  state.chainList
    .concat(state.mpcChainList)
    .concat(state.mnemonicChainList)
    .forEach((chain) => {
      mixedChainMap[chain.chainCode.toUpperCase()] = mixedChainMap[chain.chainCode.toUpperCase()] || chain;
      mixedChainMap[chain.chainCode.toLowerCase()] = mixedChainMap[chain.chainCode.toLowerCase()] || chain;
      mixedChainMap[chain.chainId] = mixedChainMap[chain.chainId] || chain;
      mixedChainId[chain.chainId] = mixedChainId[chain.chainId] || chain;
    });

  return {
    chainMap: makeChainMap(state.chainList),
    mpcChainMap: makeChainMap(state.mpcChainList),
    mnemonicChainMap: makeChainMap(state.mnemonicChainList),
    mixedList: Object.values(mixedChainId),
    mixedChainMap: mixedChainMap,
    // 云钱包的evm chains
    evmChains: state.chainList.filter((c) => (c.chainType as number) === ChainTypes.EVM),
    mnemonicEvmChains: state.mnemonicChainList.filter((c) => (c.chainType as number) === ChainTypes.EVM),
  };
};

type Compute = ReturnType<typeof compute>;

class ChainListStore extends EventBasedStore<ChainListState, Compute> {
  constructor() {
    super(
      {
        loading: false,
        chainList: [],
        mpcChainList: [],
        mnemonicChainList: [],
        showUpdateDialog: false,
      },
      compute,
      () => this.getChainList(),
    );
  }

  /**
   * 从服务端加载链的列表数据
   */
  getChainList = onlyRunOnce(async () => {
    this.setState({
      loading: true,
    });
    const [err, res] = await runAsyncSafe(retryWithFibonacci(() => getResultFromResponse(queryChainList()), 3));
    if (res) {
      this.setState({
        loading: false,
        chainList: res.walletChains.CLOUD?.itemList ?? [],
        mpcChainList: res.walletChains.MPC?.itemList ?? [],
        mnemonicChainList: res.walletChains.MNEMONIC_PHRASE?.itemList ?? [],
      });
    } else {
      this.setState({
        loading: false,
      });
    }
  });

  /**
   * 获取一个链对象（从云钱包的）
   * @param info chainId 或 chainCode
   * @returns
   */
  getChain(info?: string | number, key: 'mixedList' | 'chainList' | 'mpcChainList' | 'mnemonicChainList' = 'mixedList') {
    if (!info) {
      return undefined;
    }
    const chains = this.state[key];
    if (typeof info === 'number') {
      return chains.find((c) => Number(c.chainId) === info);
    }
    const infoLower = info.toLowerCase();
    return chains.find((c) => String(c.chainId) === infoLower || c.chainCode.toLowerCase() === infoLower || c.chainName.toLowerCase().includes(info));
  }

  getEvmRpcProvider(info: string | number) {
    const chain = this.getChain(info, 'mnemonicChainList');
    if (chain && (chain.chainType as number) === ChainTypes.EVM) {
      return getEvmRpcProvider(chain.chainCode);
    }
    return undefined;
  }
}

export const chainListStore = new ChainListStore();

export const useChainList = () => {
  const state = chainListStore.useStoreState();
  const actions = useMemo(() => pickStoreActions(chainListStore, ['getChainList', 'getChain']), []);
  return {
    ...state,
    ...actions,
  };
};
